import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { ImageAnnotator } from 'Common/utils/imageAnnotator/imageAnnotator'
import { TransformMatrixType } from 'Common/utils/markerArea/types'
import { MarkerTypes } from 'Modules/types/pages/imageReview/enums'

interface useImageAnnotatorOptions {
  onMarkerSelect?: (marker: any) => void
  onMarkerDeselect?: (marker: any) => void
  onMarkerMouseEnter?: (marker: any) => void
  onMarkerMouseLeave?: (marker: any) => void
  onMarkerCreated?: (marker: any) => void
  onMarkerEdit?: (marker: any) => any
  isMarkersInteractive?: boolean
}

export enum AnnotatorModes {
  Create = 'create',
  Select = 'select',
}

const defaultTransformMatrix = [1, 0, 0, 1, 0, 0] as TransformMatrixType

export function useImageAnnotator(options: useImageAnnotatorOptions) {
  const defaultOptions: useImageAnnotatorOptions = {
    onMarkerSelect: () => {
      return
    },
    onMarkerDeselect: () => {
      return
    },
    onMarkerMouseEnter: () => {
      return
    },
    onMarkerMouseLeave: () => {
      return
    },
    onMarkerCreated: () => {
      return
    },
    onMarkerEdit: () => {
      return
    },
    isMarkersInteractive: true,
  }
  const mergedOptions: useImageAnnotatorOptions = { ...defaultOptions, ...options }

  const mode = ref<AnnotatorModes>(AnnotatorModes.Select)
  const markerType = ref<MarkerTypes>(MarkerTypes.Rectangle)
  const ready = ref<boolean>(false)
  const scaleValue = ref<number>(0)
  const scale = computed({
    get: () => scaleValue.value,
    set: (newValue: number) => {
      scaleValue.value = newValue
      if (annotator && ready.value === true && annotator.zoom !== newValue) annotator.zoom = newValue
    },
  })
  const tranform = ref<TransformMatrixType>(defaultTransformMatrix)
  const annotator = new ImageAnnotator(null, mergedOptions)
  const annotatorCreateMarker = annotator.createNewMarker.bind(annotator)

  function onReady() {
    ready.value = true
    tranform.value = annotator.markerArea.transformMatrix
    //Handle instance where mode is set prior to marker creation
    if (mode.value === AnnotatorModes.Create) {
      annotator.createNewMarker(markerType.value)
    }
    //Handle instance where scale (a.k.a zoom) is set prior to marker creation
    if (scaleValue.value !== 0) {
      annotator.zoom = scaleValue.value
    }
  }

  function onZoomCallback(newZoomLevel: number): void {
    if (scale.value !== newZoomLevel) scale.value = newZoomLevel
  }

  function setScale(newScaleValue: number) {
    scale.value = newScaleValue
  }

  function setMode(newModeValue: AnnotatorModes) {
    mode.value = newModeValue
  }

  function createNewMarker(newMarkerType: MarkerTypes) {
    markerType.value = newMarkerType
    if (ready.value === true) annotatorCreateMarker(newMarkerType)
  }

  function reset() {
    ready.value = false
    setScale(0)
    setMode(AnnotatorModes.Select)
  }

  function bindEventListeners() {
    if (annotator) {
      annotator.onReady = onReady
      annotator.onZoom = onZoomCallback
      annotator.onMarkerCreated = (markerEvent) => {
        setMode(AnnotatorModes.Select)
        mergedOptions.onMarkerCreated(markerEvent)
      }
      annotator.onMarkerSelect = mergedOptions.onMarkerSelect
      annotator.onMarkerDeselect = mergedOptions.onMarkerDeselect
      annotator.onMarkerMouseEnter = mergedOptions.onMarkerMouseEnter
      annotator.onMarkerMouseLeave = mergedOptions.onMarkerMouseLeave
      annotator.onMarkerEdit = mergedOptions.onMarkerEdit
      annotator.onTransform = () => {
        tranform.value = annotator.markerArea.transformMatrix
      }
    }
  }

  onMounted(() => {
    bindEventListeners()
  })

  onBeforeUnmount(() => {
    annotator.close()
  })

  const composableObj = {
    mode,
    markerType,
    ready,
    scale,
    tranform,
    setScale,
    setMode,
    createNewMarker: createNewMarker,
    reset,
  }

  return Object.assign(annotator, composableObj)
}
